/**
 * Dark mode hook with localStorage persistence
 */

import { useEffect, useState } from "react";

type Theme = "light" | "dark" | "system";

function getSystemTheme(): "light" | "dark" {
  if (typeof window === "undefined") {
    return "light";
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function getEffectiveTheme(theme: Theme): "light" | "dark" {
  return theme === "system" ? getSystemTheme() : theme;
}

export function useDarkMode() {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window === "undefined") {
      return "system";
    }
    const stored = localStorage.getItem("payments_theme") as Theme | null;
    return stored || "system";
  });

  useEffect(() => {
    const root = document.documentElement;
    const effectiveTheme = getEffectiveTheme(theme);

    root.classList.remove("light", "dark");
    root.classList.add(effectiveTheme);

    localStorage.setItem("payments_theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((current) => {
      const effectiveCurrent = getEffectiveTheme(current);
      return effectiveCurrent === "dark" ? "light" : "dark";
    });
  };

  return {
    theme,
    setTheme,
    toggleTheme,
    effectiveTheme: getEffectiveTheme(theme),
  };
}
